import { useBreakpoint } from '@workspace/ui/hooks/useBreakpoint';

import { cn } from '@workspace/ui/lib/utils';
import type { ReactNode } from 'react';
import { Fragment, forwardRef, useEffect, useState } from 'react';

import type { HeaderProps } from './Header';
import { useHeaderSlots } from './useHeaderSlots';

// The threshold at which the header changes color when scrolled (px)
const SCROLL_THRESHOLD = 40;

const WrapWith = ({
  wrapper: Wrapper,
  children,
}: {
  wrapper?: React.ComponentType<{ children: ReactNode }>;
  children: ReactNode;
}) => {
  if (!Wrapper) return <Fragment>{children}</Fragment>;
  return <Wrapper>{children}</Wrapper>;
};

export const HeaderMCosmetic = forwardRef<
  HTMLDivElement,
  Omit<HeaderProps, 'variant'>
>(
  (
    {
      logo,
      logoWrapper,
      shouldChangeColorOnScroll = false,
      children,
      className,
      ...props
    },
    ref,
  ) => {
    const slots = useHeaderSlots(children);
    const { isMobile } = useBreakpoint();

    const LogoDesktop = logo.desktop;
    const LogoMobile = logo.mobile;

    const [isScrolled, setIsScrolled] = useState(false);

    // Allows the header to change color when scrolled
    useEffect(() => {
      if (isMobile || !shouldChangeColorOnScroll) return;

      const handleScroll = () =>
        setIsScrolled(window.scrollY > SCROLL_THRESHOLD);

      handleScroll();
      window.addEventListener('scroll', handleScroll, { passive: true });
      return () => window.removeEventListener('scroll', handleScroll);
    }, [isMobile, shouldChangeColorOnScroll]);

    return (
      <div
        ref={ref}
        data-scrolled={shouldChangeColorOnScroll ? isScrolled : true}
        className={cn(
          // Should use --header-height to calculate the margin bottom
          'group relative z-12 w-screen max-md:-mb-(--header-height) md:fixed md:min-h-26',
          'px-4 pt-safe-offset-4 pb-4 max-md:gap-8 md:px-10 md:pt-6',
          'grid grid-cols-[auto_auto] justify-between max-md:items-center md:grid-cols-[220px_1fr_220px]',
          'border-b border-b-graphic-transparent-white-25 transition-colors duration-300',
          'data-[scrolled=true]:border-b-stroke-tertiary data-[scrolled=true]:bg-white',
          '*:h-max',
          className,
        )}
        {...props}
      >
        <div className="flex gap-3 truncate">
          {LogoMobile && isMobile && (
            <WrapWith wrapper={logoWrapper}>
              <LogoMobile
                aria-label="logo"
                className="size-7 text-graphic-regular-white"
              />
            </WrapWith>
          )}
          {slots.address}
        </div>
        {!isMobile && (
          <div className="flex flex-col items-center gap-3">
            {LogoDesktop && (
              <WrapWith wrapper={logoWrapper}>
                <LogoDesktop
                  aria-label="logo"
                  className="h-[27px] w-50 text-graphic-regular-white transition-colors duration-300 group-data-[scrolled=true]:text-graphic-regular-brand"
                />
              </WrapWith>
            )}
            {slots.mainShopCategories}
          </div>
        )}
        <div className="flex gap-5 justify-self-end text-graphic-regular-white transition-colors duration-300 group-data-[scrolled=true]:text-text-primary">
          {slots.navigation}
        </div>
      </div>
    );
  },
);
HeaderMCosmetic.displayName = 'HeaderMCosmetic';
